<script type="text/javascript">
	import {onMount} from "svelte"
	import { createEventDispatcher } from 'svelte';
	import {get, writable} from "svelte/store"

	import "./TierForm.scss"
	import API from "../../API/API"
	import AdminStore from "./Store"
	import AlertType from "../../Alerts/AlertType"
	import AlertTypeDescriptions from "../../Alerts/AlertTypeDescriptions"
	import Feature from "../../Branding/Feature";
	import FeatureDescription from "../../Branding/FeatureDescription";

	import Form from './Form.svelte';
	import List, {Group, Item, Graphic, Meta, Label as ListLabel, Separator, Subheader, Text, PrimaryText, SecondaryText} from '@smui/list';
	import Switch from '@smui/switch';
	import Checkbox from '@smui/checkbox';
	import FormField from '@smui/form-field';
	import Button, {Label} from '@smui/button';
	import DataTable, {Head, Body, Row, Cell} from '@smui/data-table';
	import Select, {Option} from '@smui/select';
	import Textfield, {Input, Textarea} from "@smui/textfield";
	import HelperText from "@smui/textfield/helper-text/index";
	import Icon from "@smui/textfield/icon/index";
	import SpinnerButton from "./SpinnerButton.svelte";
	import LimitCheckbox from "./LimitCheckbox.svelte";

	import { formatUnixTimestamp } from "../../Helpers/Misc/timeHelper";

	export let tier; // Tier Model

	const dispatch = createEventDispatcher(); // submit | reset | delete

	const Loading = {
		NONE: -1,
		SAVING_TIER: 1,
		DELETING_TIER: 2
	};

	let allRois = AdminStore.cms.rois;
	let allAlertTypes = AlertType.all();
	let allFeatures = Feature.all();

	let createEditable = (tier) => {
		let editable = {
			name: tier.name,
			notes: tier.notes,
			user_limit: tier.userLimit,
			alert_type_limit: tier.alertTypeLimit,
			roi_per_alert_type_limit: tier.roiPerAlertTypeLimit,
			alert_types: tier.alertTypes.map((item) => { return item.code; }),
			rois: tier.rois.map((item) => { return item.id; }),
			features: JSON.parse(JSON.stringify(tier.features)),

			meta: {
				userLimitChecked: tier.userLimit != -1,
				alertTypeLimitChecked: tier.alertTypeLimit != -1,
				roiLimitChecked: tier.roiPerAlertTypeLimit != -1
			}
		};
		// remove any features that are deprecated!
		for(let featureKey in editable.features) {
			if (allFeatures.indexOf(featureKey) == -1) {
				console.log('removing feature', featureKey);
				delete editable.features[featureKey];
			}
		}
		return editable;
	}
	let editable = createEditable(tier);

	let form = null;
	let loading = writable(Loading.NONE);
	$: if (form) { form.setReadOnly($loading !== Loading.NONE); }



	let onReset = () => {
		editable = createEditable(tier);
		console.log("init/reset editable", editable);
		dispatch('reset');
	}
	let onSubmit = () => {
		let useUserLimit = parseInt((editable.meta.userLimitChecked)?editable.user_limit:-1);
		let useAlertTypeLimit = parseInt((editable.meta.alertTypeLimitChecked)?editable.alert_type_limit:-1);
		let useROILimit = parseInt((editable.meta.roiLimitChecked)?editable.roi_per_alert_type_limit:-1);

		if (isNaN(useUserLimit)) {
			form.snackbar.start("Invalid User Limit.", "error");
			return;
		}
		else if (isNaN(useAlertTypeLimit)) {
			form.snackbar.start("Invalid Alert Type Limit.", "error");
			return;
		}
		else if (isNaN(useROILimit)) {
			form.snackbar.start("Invalid ROI Limit.", "error");
			return;
		}

		if (editable.name.length <= 0) {
			form.snackbar.start("Tier name must be set", "error");
			return;
		}

		let data = {
			name: editable.name,
			notes: editable.notes,
			user_limit: useUserLimit,
			alert_type_limit: useAlertTypeLimit,
			roi_per_alert_type_limit: useROILimit,
			alert_types: editable.alert_types,
			rois: editable.rois,
			features: editable.features
		};
		console.log('submit data', data);

		// Create/Update Tier
		let n = tier.isNew();
		let url = n ? "admin/tiers/create" : "admin/tiers/" + tier.id;
		$loading = Loading.SAVING_TIER;
		API.put(url,
			data
		).then((response) =>{
			console.log("response", response);
			if (tier.isNew()) {
				AdminStore.actions.addTier(tier);
			}
			tier.setData(response);
			onReset();
			form.snackbar.start("Tier " + (n ? "created" : "updated") + " successfully.", "success");
			dispatch('submit', {tier});
		}).catch((e)=>{
			console.error(e.data.title, e.data.detail);
			form.snackbar.start("Tier " + (n ? "create" : "update") + " failed.", "error");
		}).finally(() =>{
			$loading = Loading.NONE;
		})
	};
	let onDelete = () => {

		AdminStore.cms.confirmDialog.onConfirm = () => {
			$loading = Loading.DELETING_TIER;
			API.delete("admin/tiers/" + tier.id)
				.then((response)=>{
					console.log(response);
					form.snackbar.start("Tier deleted successfully.", "success");
					AdminStore.actions.deleteTier(tier);
					dispatch('delete');
				})
				.catch((e)=>{
					console.error(e.data.title, e.data.detail);
					form.snackbar.start("Tier deletion failed.", "error");
				})
				.finally(()=>{
					$loading = Loading.NONE;
					AdminStore.cms.confirmDialog.visible.set(false);
				});
		}
		AdminStore.cms.confirmDialog.visible.set(true);
	}

	let onSelectAllAlertTypes = () => {
		editable.alert_types = [...allAlertTypes];
	}
	let onDeselectAllAlertTypes = () => {
		editable.alert_types = [];
	}

	let onSelectAllROIs = () => {
		const keys = Object.keys($allRois)
		let roiIds = keys.map((key) => {
			return $allRois[key].id;
		});
		editable.rois = roiIds;
	}
	let onDeselectAllROIs = () => {
		editable.rois = [];
	}


	onMount(()=>{
		onReset();
	});

</script>

<Form bind:this={form} id='admin-tier-form'>
	{#if tier.isNew()}
		<h2>New Tier</h2>
	{:else}
		<h2>Existing Tier</h2>
	{/if}

	<DataTable table$aria-label="Tier">
		<Head>
			<Row>
				<Cell></Cell>
				<Cell></Cell>
			</Row>
		</Head>
		<Body>
			<Row>
				<Cell>Name</Cell>
				<Cell>
					<Textfield dense variant="outlined" bind:value={editable.name}/>
				</Cell>
			</Row>
			<Row>
				<Cell>Notes</Cell>
				<Cell>
					<textarea class='long-textarea' bind:value={editable.notes}/>
				</Cell>
			</Row>
			<Row>
				<Cell>User Limit</Cell>
				<Cell>
					<LimitCheckbox label="How many Users?" name="user_limit" bind:checked={editable.meta.userLimitChecked} bind:value={editable.user_limit}/>
				</Cell>
			</Row>
			<Row>
				<Cell>Alert Type Limit</Cell>
				<Cell>
					<LimitCheckbox label="How many Alert Types?" name="alert_type_limit" bind:checked={editable.meta.alertTypeLimitChecked} bind:value={editable.alert_type_limit}/>
				</Cell>
			</Row>
			<Row>
				<Cell>ROI (Per Alert Type) Limit:</Cell>
				<Cell>
					<LimitCheckbox label="How many ROIs?" name="roi_per_alert_type_limit" bind:checked={editable.meta.roiLimitChecked} bind:value={editable.roi_per_alert_type_limit}/>
				</Cell>
			</Row>
			<Row>
				<Cell class='cell-align-top'>Access to Alert Types</Cell>
				<Cell>
					<List class="demo-list" checklist>
						{#each allAlertTypes as alertType}
							<Item>
								<ListLabel>
									<span class="shortcode">{alertType}</span>
									&nbsp;{AlertTypeDescriptions[alertType]}
								</ListLabel>
								<Meta>
									<!-- <Checkbox bind:group={$alertTypesSelected} value={alertType} /> -->
									<input type='checkbox' name='alert_types' value={alertType} bind:group={editable.alert_types}>
								</Meta>
							</Item>
						{/each}
					</List>

					<Button on:click={onSelectAllAlertTypes} color="primary" class="bg-info" variant="unelevated" ><Label>Select All</Label></Button><br/>
					<Button on:click={onDeselectAllAlertTypes} color="primary" class="bg-info" variant="unelevated" style="margin-bottom:10px;" ><Label>Deselect All</Label></Button>

				</Cell>
			</Row>

			<Row>
				<Cell>Access to ROIs</Cell>
				<Cell>
					{#each Object.keys($allRois) as roiKey}
						<Item>
							<ListLabel>
								{$allRois[roiKey].name}
							</ListLabel>
							<Meta>
								<input type='checkbox' name='features' value={$allRois[roiKey].id} bind:group={editable.rois}>
							</Meta>
						</Item>
					{/each}
					<Button on:click={onSelectAllROIs} color="primary" class="bg-info" variant="unelevated"><Label>Select All</Label></Button><br/>
					<Button on:click={onDeselectAllROIs} color="primary" class="bg-info" variant="unelevated" style="margin-bottom:10px;"><Label>Deselect All</Label></Button>
				</Cell>
			</Row>

			<Row>
				<Cell class='cell-align-top'>Features</Cell>
				<Cell>
					<List class="demo-list" checklist>
						{#each allFeatures as feature}
							<Item>
								<ListLabel>
									<span class="shortcode">{feature}</span><br/>
									{FeatureDescription[feature]}
								</ListLabel>
								<Meta>
									<!-- <Checkbox bind:group={$featuresSelected} value={featureKey} /> -->
									<input type='checkbox' name='features' value={1} bind:checked={editable.features[feature]}>
								</Meta>
							</Item>
						{/each}
					</List>
				</Cell>
			</Row>

		</Body>
	</DataTable>


	{#if $loading == Loading.SAVING_TIER}
		<SpinnerButton/>
	{:else}
		<Button on:click={onSubmit} color="primary" variant="unelevated"><Label>{((!tier.isNew())?"Update Tier":"Add Tier")}</Label></Button>
	{/if}

	<Button on:click={onReset} color="primary" class="bg-info" variant="unelevated"><Label>Reset</Label></Button>

	<hr/>

	{#if tier.id > 1}
		<Button color="secondary" variant="unelevated" class="bg-error" on:click={onDelete}><Label>Delete Tier</Label></Button>
	{/if}

	<hr/>

	<h1>TODO:</h1>
	<ul>
		<li>Company List</li>
		<li>Cost per month?</li>
		<li>Cost per additional user?</li>
		<li>Other benefits?</li>
	</ul>

</Form>
